import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionIcon } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { api } from '../../apiClient';
import MobileAppHeader, { CH, IconBtn, Logo, I } from './MobileAppHeader';

// 進捗リング
const Ring = ({ correct = 0, incorrect = 0, total = 1, size = 44, sw = 4 }) => {
  const r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  const cP = total > 0 ? (correct / total) * c : 0;
  const iP = total > 0 ? (incorrect / total) * c : 0;
  const pct = total > 0 ? Math.round(((correct + incorrect) / total) * 100) : 0;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={CH.border || '#f0eee6'} strokeWidth={sw} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#099268" strokeWidth={sw}
          strokeDasharray={`${cP} ${c}`} strokeLinecap="round" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#c03727" strokeWidth={sw}
          strokeDasharray={`${iP} ${c}`} strokeDashoffset={-cP} strokeLinecap="round" />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: CH.mono, fontSize: 10, fontWeight: 600, color: CH.ink,
      }}>{pct}%</div>
    </div>
  );
};

// バッジ
const Badge = ({ color = 'gray', children }) => {
  const palettes = {
    orange: { bg: '#fff4e6', fg: '#e8590c' },
    blue:   { bg: '#e7f5ff', fg: '#1c7ed6' },
    gray:   { bg: '#f1f3f5', fg: '#868e96' },
    violet: { bg: '#f3f0ff', fg: '#7048e8' },
    green:  { bg: '#ebfbee', fg: '#2f9e44' },
    teal:   { bg: '#e6fcf5', fg: '#099268' },
    red:    { bg: '#fff0ee', fg: '#c03727' },
  };
  const p = color.startsWith('#')
    ? { bg: color + '22', fg: color }
    : palettes[color] || palettes.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: p.bg, color: p.fg,
      fontFamily: CH.serif, fontSize: 11, fontWeight: 500,
      padding: '2px 8px', borderRadius: 4, lineHeight: 1.4,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

// 資格カード
const QualCard = ({ cert, progress, onClick }) => {
  const vendor = cert.vendor?.toLowerCase();
  const vColor = vendor === 'aws' ? '#e8590c' : vendor === 'google' ? '#34A853' : '#1c7ed6';
  const vLabel = vendor === 'aws' ? 'AWS' : vendor === 'microsoft' ? 'Microsoft' : vendor === 'google' ? 'Google' : vendor;
  const statusMeta = {
    learning:  { label: 'Learning',  color: 'blue'   },
    certified: { label: 'Certified', color: 'green'  },
    enrolled:  { label: 'Enrolled',  color: 'violet' },
    retired:   { label: 'Retired',   color: 'gray'   },
  }[cert.status] || null;

  const correct   = Number(progress?.correct   ?? 0);
  const incorrect = Number(progress?.incorrect ?? 0);
  const total     = Number(progress?.total     ?? 0);

  return (
    <div onClick={onClick} style={{
      width: 240, flexShrink: 0,
      background: 'var(--app-color-surface)',
      border: '1px solid var(--app-color-border)',
      borderRadius: 10, padding: 14,
      display: 'flex', flexDirection: 'column', gap: 10,
      cursor: 'pointer',
      ...(cert.status === 'learning' ? { boxShadow: `0 0 0 2px #c96442` } : {}),
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Badge color={vColor}>{vLabel}</Badge>
          {statusMeta && <Badge color={statusMeta.color}>{statusMeta.label}</Badge>}
        </div>
        <Ring correct={correct} incorrect={incorrect} total={total} />
      </div>
      <div>
        {cert.formal_name && (
          <div style={{ fontFamily: CH.serif, fontSize: 15, fontWeight: 500, color: CH.ink, letterSpacing: -0.2, lineHeight: 1.3 }}>
            {cert.formal_name}
          </div>
        )}
        <div style={{ fontSize: 11, color: CH.muted, lineHeight: 1.4, marginTop: cert.formal_name ? 3 : 0 }}>
          {cert.name}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, fontSize: 11, fontFamily: CH.mono, color: CH.muted }}>
        <span style={{ color: '#099268' }}>● {correct}</span>
        <span style={{ color: '#c03727' }}>● {incorrect}</span>
        <span>/ {total}</span>
      </div>
    </div>
  );
};

const VENDOR_META = {
  aws:       { label: 'AWS',       defaultOrder: 0 },
  microsoft: { label: 'Microsoft', defaultOrder: 1 },
  google:    { label: 'Google',    defaultOrder: 2 },
};

const getAWSLevel = (cert) => {
  const n = `${cert.name} ${cert.formal_name ?? ''}`.toLowerCase();
  if (n.includes('foundational') || n.includes('cloud practitioner')) return 0;
  if (n.includes('associate'))    return 1;
  if (n.includes('professional')) return 2;
  if (n.includes('specialty'))    return 3;
  return 4;
};

const getMicrosoftLevel = (cert) => {
  const n = `${cert.name} ${cert.formal_name ?? ''}`.toLowerCase();
  if (n.includes('fundamentals')) return 0;
  if (n.includes('associate'))    return 1;
  if (n.includes('expert'))       return 2;
  if (n.includes('specialty') || n.includes('specialist')) return 3;
  return 4;
};

const getGoogleLevel = (cert) => {
  const n = `${cert.name} ${cert.formal_name ?? ''}`.toLowerCase();
  if (n.includes('digital leader')) return 0;
  if (n.includes('associate'))      return 1;
  if (n.includes('professional'))   return 2;
  return 3;
};

const sortCerts = (certs, vendorKey) => {
  const getLevel = vendorKey === 'aws' ? getAWSLevel : vendorKey === 'google' ? getGoogleLevel : getMicrosoftLevel;
  return [...certs].sort((a, b) => {
    const aL = a.status === 'learning' ? 0 : 1;
    const bL = b.status === 'learning' ? 0 : 1;
    if (aL !== bL) return aL - bL;
    const aR = a.status === 'retired' ? 1 : 0;
    const bR = b.status === 'retired' ? 1 : 0;
    if (aR !== bR) return aR - bR;
    return getLevel(a) - getLevel(b);
  });
};

// ベンダーセクション
const Section = ({ title, subtitle, onUp, onDown, isFirst, isLast, children }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 20px 10px',
    }}>
      <div>
        <span style={{ fontFamily: CH.serif, fontSize: 20, fontWeight: 500, color: CH.ink, letterSpacing: -0.3 }}>{title}</span>
        <span style={{ fontSize: 11, color: CH.dim, marginLeft: 8, fontFamily: CH.mono }}>{subtitle}</span>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <ActionIcon size="xs" variant="subtle" disabled={isFirst} onClick={onUp} aria-label="上へ">
          <IconChevronUp size={14} />
        </ActionIcon>
        <ActionIcon size="xs" variant="subtle" disabled={isLast} onClick={onDown} aria-label="下へ">
          <IconChevronDown size={14} />
        </ActionIcon>
      </div>
    </div>
    <div style={{
      display: 'flex', gap: 12, padding: '4px 20px 16px',
      overflowX: 'auto', scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
    }}>{children}</div>
  </div>
);

function MobileHomeScreen({ user, onLogout }) {
  const navigate = useNavigate();
  const [certData, setCertData]     = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [resumeData, setResumeData]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [vendorOrder, setVendorOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('vendorOrder');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [certs, progress] = await Promise.all([
          api.get('/user-certification-statuses'),
          api.get('/user-certifications/progress'),
        ]);
        setCertData(certs ?? []);
        const pMap = {};
        for (const p of progress ?? []) pMap[p.certification_id] = p;
        setProgressMap(pMap);
      } catch (e) {
        console.error('データ取得失敗:', e);
      }

      try {
        // 最後にアクセスした資格コードを取得して resume を構成
        const stored = Object.keys(localStorage)
          .filter(k => k.startsWith('lastQuestion_'))
          .map(k => ({ code: k.replace('lastQuestion_', ''), id: localStorage.getItem(k) }));
        if (stored.length > 0) {
          // 複数あれば最初のものを使用
          setResumeData(stored[0]);
        }
      } catch {}

      setLoading(false);
    };
    fetchAll();
  }, []);

  // ベンダー別グループ化
  const byVendor = certData.reduce((acc, c) => {
    const v = (c.vendor ?? 'other').toLowerCase();
    if (!acc[v]) acc[v] = [];
    acc[v].push(c);
    return acc;
  }, {});

  const allVendors = Object.keys(byVendor);
  const resolvedOrder = vendorOrder
    ? [...vendorOrder.filter(v => allVendors.includes(v)), ...allVendors.filter(v => !vendorOrder.includes(v))]
    : [...allVendors].sort((a, b) => (VENDOR_META[a]?.defaultOrder ?? 99) - (VENDOR_META[b]?.defaultOrder ?? 99));

  const moveVendor = (vendor, dir) => {
    const order = [...resolvedOrder];
    const idx = order.indexOf(vendor);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= order.length) return;
    [order[idx], order[swap]] = [order[swap], order[idx]];
    setVendorOrder(order);
    localStorage.setItem('vendorOrder', JSON.stringify(order));
  };

  const today = new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });

  return (
    <div style={{ background: 'var(--mantine-color-body)', minHeight: '100vh', fontFamily: CH.serif }}>
      <MobileAppHeader
        left={<div onClick={() => navigate('/Study')} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} role="link" aria-label="ホームへ移動"><Logo size={24} /><span style={{ fontSize: 16, fontWeight: 500, color: CH.ink, letterSpacing: -0.3 }}>CloudHub</span></div>}
        right={
          <IconBtn onClick={() => navigate('/Settings')} title="アカウント">
            {I.user}
          </IconBtn>
        }
      />

      {/* 挨拶 */}
      <div style={{ padding: '20px 20px 8px' }}>
        <div style={{ fontSize: 11, fontFamily: CH.mono, color: CH.dim, textTransform: 'uppercase', letterSpacing: 0.5 }}>{today}</div>
        <div style={{ fontSize: 22, color: CH.ink, letterSpacing: -0.4, lineHeight: 1.2, marginTop: 4 }}>おかえりなさい。</div>
        <div style={{ fontSize: 13, color: CH.muted, marginTop: 4 }}>続きから再開しますか？</div>
      </div>

      {/* 前回の続きカード */}
      {resumeData && (
        <div style={{ padding: '12px 20px 20px' }}>
          <div
            onClick={() => navigate(`/Study/${resumeData.code}`)}
            style={{
              background: 'var(--app-color-surface)',
              borderRadius: 12, padding: 14,
              border: '1px solid var(--app-color-border)',
              borderLeft: '3px solid #c96442',
              display: 'flex', gap: 12, alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 8, background: '#fdf5f2',
              color: '#c96442', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>{I.play}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: CH.dim, fontFamily: CH.mono, marginBottom: 2 }}>前回の続き</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: CH.ink }}>
                {resumeData.code?.toUpperCase()}
              </div>
            </div>
            <div style={{ color: CH.dim }}>{I.chevR}</div>
          </div>
        </div>
      )}

      {/* ベンダー別セクション */}
      {loading ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: CH.dim, fontFamily: CH.mono, fontSize: 13 }}>
          読み込み中...
        </div>
      ) : (
        resolvedOrder.filter(v => byVendor[v]?.length > 0).map((v, idx, arr) => (
          <Section
            key={v}
            title={VENDOR_META[v]?.label ?? v}
            subtitle={`${byVendor[v].length} qualifications`}
            onUp={() => moveVendor(v, 'up')}
            onDown={() => moveVendor(v, 'down')}
            isFirst={idx === 0}
            isLast={idx === arr.length - 1}
          >
            {sortCerts(byVendor[v], v).map((cert) => (
              <QualCard
                key={cert.id}
                cert={cert}
                progress={progressMap[cert.id] ?? null}
                onClick={() => navigate(`/Study/${cert.code}`)}
              />
            ))}
          </Section>
        ))
      )}

      <div style={{ height: 40 }} />
    </div>
  );
}

export default MobileHomeScreen;
